import React, { useState } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Plus, Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth, hasMinRole } from "@/contexts/AuthContext";
import { StatusBadge } from "@/components/app/ui-bits";
import AiExtractDialog from "@/components/app/AiExtractDialog";
import { Loading } from "./_shared";
import { StaffForm } from "./_forms";
import StaffFullProfile from "./StaffFullProfile";

export default function StaffDirectory({ staff, reload, loading, newId, highlightRef, classForId }) {
  const { user } = useAuth();
  const canManage = hasMinRole(user, "manager");
  const [selected, setSelected] = useState(null);

  const createStaff = async (form) => {
    try { await api.post("/hr/staff", form); toast.success("Staff added"); reload(); }
    catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display text-base font-semibold">All Staff ({staff.length})</h3>
        {canManage && (
          <div className="flex flex-wrap items-center gap-2">
            <AiExtractDialog
              module="hr"
              onSaved={reload}
              trigger={
                <Button variant="outline" data-testid="hr-ai-extract-btn">
                  <Sparkle size={14} className="mr-2" weight="fill" /> AI Extract
                </Button>
              }
            />
            <StaffForm initial={null} onSave={createStaff}>
              <Button className="btn-primary-blue" data-testid="new-staff-btn"><Plus size={14} /> New Staff</Button>
            </StaffForm>
          </div>
        )}
      </div>
      {loading ? <Loading /> : staff.length === 0 ? (
        <div className="dc-card p-10 text-center text-sm text-slate-500">No staff yet</div>
      ) : (
        <div className="dc-card overflow-hidden">
          <table className="hidden w-full md:table">
            <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-5 py-3">Name</th><th className="px-5 py-3">Code</th><th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Designation</th><th className="px-5 py-3">Dept</th>
                <th className="px-5 py-3">Salary</th><th className="px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {staff.map((s) => (
                <tr
                  key={s.id}
                  ref={s.id === newId ? highlightRef : null}
                  className={`text-sm cursor-pointer hover:bg-slate-50 ${classForId(s.id)}`}
                  onClick={() => setSelected(s)}
                  data-testid={`staff-row-${s.id}`}
                >
                  <td className="px-5 py-3 font-semibold text-slate-900">
                    <div className="flex flex-wrap items-center gap-2">
                      <span>{s.full_name}</span>
                      {s.source === "ai_extraction" && (
                        <span className="dc-ai-chip" title="Imported via AI Extractor">AI</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 font-mono-alt text-xs">{s.employee_code || "—"}</td>
                  <td className="px-5 py-3 text-xs"><span className="font-mono-alt uppercase tracking-wider">{s.staff_type?.replace("_"," ")}</span></td>
                  <td className="px-5 py-3 text-slate-700">{s.designation || "—"}</td>
                  <td className="px-5 py-3 text-slate-700">{s.department || "—"}</td>
                  <td className="px-5 py-3 font-mono-alt tabular-nums">{formatCurrency(s.salary)}</td>
                  <td className="px-5 py-3"><StatusBadge status={s.status === "active" ? "active" : s.status === "separated" ? "rejected" : "pending"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="divide-y divide-slate-100 md:hidden">
            {staff.map((s) => (
              <li
                key={s.id}
                ref={s.id === newId ? highlightRef : null}
                className={`p-4 cursor-pointer hover:bg-slate-50 ${classForId(s.id)}`}
                onClick={() => setSelected(s)}
                data-testid={`staff-card-${s.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="truncate font-semibold text-slate-900">{s.full_name}</div>
                      {s.source === "ai_extraction" && (
                        <span className="dc-ai-chip" title="Imported via AI Extractor">AI</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{s.employee_code} · {s.designation}</div>
                  </div>
                  <StatusBadge status={s.status === "active" ? "active" : "pending"} />
                </div>
                <div className="mt-1 text-xs text-slate-600">{formatCurrency(s.salary)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogTitle className="sr-only">Employee profile · {selected?.full_name || ""}</DialogTitle>
          <DialogDescription className="sr-only">Complete profile of {selected?.full_name || ""} including contracts, salary, performance, leaves and disciplinary history.</DialogDescription>
          {selected && <StaffFullProfile staffId={selected.id} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
